import { ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useNavigate } from 'react-router-dom';
import {
  ComponentClass,
  createElement,
  FunctionComponent,
  ReactNode,
  useMemo,
  useState,
} from 'react';
import { Formik } from 'formik';
import { AnyObject, ObjectSchema } from 'yup';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Stack,
  Stepper,
  Step,
  StepLabel,
  Box,
  Alert,
} from '@mui/material';
import { NewQuote } from '@/types/quote';
import useResponsive from '@/hooks/useResponsive';
// import { motion } from 'framer-motion';

interface Step {
  title: string;
  stepTitle?: string; // TODO: Remove conditional
  component:
  | string
  | FunctionComponent<unknown>
  | ComponentClass<unknown, unknown>;
  schema: ObjectSchema<object, AnyObject, object, ''>;
}

interface Props {
  headerTitle?: string;
  steps: Step[];
  onSubmit?: () => void;
  disabled?: boolean;
  hideStepper?: boolean;
}

const FormikStepper = ({ headerTitle, steps, onSubmit, disabled, hideStepper }: Props) => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const { isMobile } = useResponsive();

  const isLastStep = useMemo(() => {
    return activeStep === steps.length - 1;
  }, [activeStep, steps.length]);

  type FormValues = Omit<NewQuote, 'createdAt'>;

  const activeSchema = steps[activeStep].schema;

  const handleNext = (values: FormValues) => {
    if (activeSchema) {
      try {
        activeSchema.validateSync(values);
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setErrorMessage('');
      } catch (error) {
        if (error.name === 'ValidationError') {
          setErrorMessage(error.message);
        }
      }
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <Formik
      initialValues={{}}
      onSubmit={(values: FormValues) => {
        if (activeSchema) {
          try {
            activeSchema.validateSync(values);
            setErrorMessage('');
            onSubmit(values);
          } catch (error) {
            if (error.name === 'ValidationError') {
              setErrorMessage(error.message);
            }
          }
        }
      }}
    >
      {({ handleSubmit, values }) => (
        <>
          <AppBar position="static">
            <Toolbar>
              <Typography variant="h3" sx={{ flexGrow: 1 }}>
                {headerTitle}
              </Typography>
              <Button
                color="white"
                variant="outlined"
                sx={{
                  borderRadius: '4px',
                }}
                onClick={() => {
                  navigate('/');
                }}
              >
                Cancelar
              </Button>
            </Toolbar>
          </AppBar>

          <Container sx={{ padding: 4 }}>
            <Stack spacing={3} flex={1}>
              {!hideStepper || isMobile ? (
                <Box>
                  <Stepper
                    activeStep={activeStep}
                    alternativeLabel={isMobile}
                  // orientation={isMobile ? 'vertical' : 'horizontal'}
                  >
                    {steps.map(({ title }) => {
                      const stepProps: { completed?: boolean } = {};
                      const labelProps: {
                        optional?: ReactNode;
                      } = {};

                      return (
                        <Step
                          key={title}
                          {...stepProps}
                          sx={{
                            '& .MuiStepLabel-iconContainer .Mui-completed': {
                              color: 'green',
                            },
                          }}
                        >
                          <StepLabel {...labelProps}>{title}</StepLabel>
                        </Step>
                      );
                    })}
                  </Stepper>
                </Box>
              ) : null}

              {steps[activeStep]?.stepTitle ? (
                <Typography variant="h3" fontSize={'32px'}>
                  {steps[activeStep].stepTitle}
                </Typography>
              ) : null}

              {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

              <Box
                sx={{
                  alignItems: { xs: 'center' },
                }}
              // component={motion.div}
              // initial={{
              //   opacity: 0,
              //   // x: direction === 'foreward' ? '100vw' : '-100vw'
              //   x: '100vw',
              // }}
              // animate={{
              //   opacity: 1,
              //   x: 0,
              // }}
              // transition={{
              //   delay: 0.25,
              //   duration: 0.5,
              // }}
              >
                {createElement(steps[activeStep].component)}
              </Box>

              <Stack spacing={2} direction={'row'}>
                <Button
                  variant="outlined"
                  startIcon={<HugeiconsIcon icon={ArrowLeft01Icon} />}
                  onClick={() => handleBack()}
                  sx={{
                    borderRadius: '4px',
                  }}
                  disabled={activeStep === 0}
                  size="large"
                  fullWidth={!!isMobile}
                >
                  Anterior
                </Button>
                <Button
                  endIcon={<HugeiconsIcon icon={ArrowRight01Icon} />}
                  onClick={isLastStep ? handleSubmit : () => handleNext(values)}
                  sx={{
                    borderRadius: '4px',
                  }}
                  disabled={disabled}
                  size="large"
                  fullWidth={!!isMobile}
                >
                  {isLastStep ? 'Finalizar' : 'Siguiente'}
                </Button>
              </Stack>
            </Stack>
          </Container>
        </>
      )}
    </Formik>
  );
};

export default FormikStepper;
